import React, { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons'
import { Link } from 'react-router-dom'

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import SalesButton from '../../components/_atoms/SalesButton/SalesButton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import { colors, fontSizes, fontWeights } from '../../constants/salesStyles'
import {
    useGetAnalyticsDealsBySellerMutation,
    useGetAnalyticsDealsBySourceMutation,
    useGetAnalyticsDealsMutation,
    useGetDealStatusDictQuery,
} from '../../modules/store/dochubApi/dochubApi'
import { DealStatus } from '../../modules/store/dochubApi/dochubTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { Marketplaces, MarketplacesKeys } from '../../types/part/partTypes'

import styles from './styles.module.scss'

type PeriodMode = 'month' | 'range'
type DateType = 'created' | 'sold'
type SortField = 'date' | 'marketplace' | 'count' | 'sum'
type SellerSortField = 'manager' | 'count' | 'sum'
type SourceSortField = 'source' | 'count' | 'sum'
type SortDirection = 'asc' | 'desc'

const toDate = (value: string) => new Date(`${value}T00:00:00`)
const formatAxisDate = (value: string) => {
    const date = toDate(value)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}.${month}`
}

const formatMonthValue = (date: Date) => {
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    return `${year}-${month}`
}

const monthLabel = (monthValue: string) => {
    const [year, month] = monthValue.split('-').map(Number)
    if (!year || !month) return ''
    return new Date(year, month - 1, 1).toLocaleString('ru-RU', {
        month: 'long',
        year: 'numeric',
    })
}

const getCurrentMonthValue = () => formatMonthValue(new Date())

const getMonthRange = (monthValue: string) => {
    const [year, month] = monthValue.split('-').map(Number)
    if (!year || !month) return null

    const monthStart = `${year}-${String(month).padStart(2, '0')}-01`
    const daysInMonth = new Date(year, month, 0).getDate()
    const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`

    return { from: monthStart, to: monthEnd }
}

const marketplaceLabel = (marketplace: string) => Marketplaces[marketplace as MarketplacesKeys] || marketplace

const AnalyticsDeals = () => {
    const [periodMode, setPeriodMode] = useState<PeriodMode>('month')
    const [monthValue, setMonthValue] = useState(getCurrentMonthValue())
    const [rangeFrom, setRangeFrom] = useState('')
    const [rangeTo, setRangeTo] = useState('')
    const [dateType, setDateType] = useState<DateType>('created')
    const [selectedStatus, setSelectedStatus] = useState<DealStatus>(DealStatus.issued)
    const [selectedMarketplaces, setSelectedMarketplaces] = useState<MarketplacesKeys[]>(
        Object.values(MarketplacesKeys) as MarketplacesKeys[],
    )
    const [sortField, setSortField] = useState<SortField>('date')
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
    const [sellerSortField, setSellerSortField] = useState<SellerSortField>('sum')
    const [sellerSortDirection, setSellerSortDirection] = useState<SortDirection>('desc')
    const [sourceSortField, setSourceSortField] = useState<SourceSortField>('sum')
    const [sourceSortDirection, setSourceSortDirection] = useState<SortDirection>('desc')
    const [getAnalyticsDeals, { data: analyticsDealsData, isLoading: isAnalyticsDealsLoading, isError }] =
        useGetAnalyticsDealsMutation()
    const [
        getAnalyticsDealsBySeller,
        {
            data: analyticsDealsBySellerData,
            isLoading: isAnalyticsDealsBySellerLoading,
            isError: isAnalyticsDealsBySellerError,
        },
    ] = useGetAnalyticsDealsBySellerMutation()
    const [
        getAnalyticsDealsBySource,
        {
            data: analyticsDealsBySourceData,
            isLoading: isAnalyticsDealsBySourceLoading,
            isError: isAnalyticsDealsBySourceError,
        },
    ] = useGetAnalyticsDealsBySourceMutation()
    const { data: dealStatusDictData } = useGetDealStatusDictQuery()
    const allMarketplaces = Object.values(MarketplacesKeys) as MarketplacesKeys[]
    const allMarketplacesSelected = selectedMarketplaces.length === allMarketplaces.length

    const selectedPeriod = useMemo(() => {
        if (periodMode === 'month') {
            return getMonthRange(monthValue)
        }
        if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) {
            return null
        }
        return { from: rangeFrom, to: rangeTo }
    }, [monthValue, periodMode, rangeFrom, rangeTo])

    useEffect(() => {
        if (!selectedPeriod) return

        const requestParams = {
            period: selectedPeriod,
            marketplace: allMarketplacesSelected ? [] : selectedMarketplaces,
            status: [selectedStatus],
            dateType,
        }

        getAnalyticsDeals(requestParams)
        getAnalyticsDealsBySeller(requestParams)
        getAnalyticsDealsBySource(requestParams)
    }, [
        allMarketplacesSelected,
        dateType,
        getAnalyticsDeals,
        getAnalyticsDealsBySeller,
        getAnalyticsDealsBySource,
        selectedMarketplaces,
        selectedPeriod,
        selectedStatus,
    ])

    const chartByDay = selectedPeriod ? analyticsDealsData?.data?.chartByDay || [] : []
    const dealsByMarketplace = selectedPeriod ? analyticsDealsData?.data?.chartByMarketplace || [] : []
    const dealsBySeller = selectedPeriod ? analyticsDealsBySellerData?.data || [] : []
    const dealsBySource = selectedPeriod ? analyticsDealsBySourceData?.data || [] : []
    const tableRows = selectedPeriod ? analyticsDealsData?.data?.table || [] : []
    const dealsCountTotal = analyticsDealsData?.data?.totals?.dealsCount || 0
    const dealsSumTotal = analyticsDealsData?.data?.totals?.dealsSum || 0
    const dealsCountLabel = dealsCountTotal.toLocaleString('ru-RU')
    const dealsSumLabel = Math.round(dealsSumTotal).toLocaleString('ru-RU')
    const isChartsLoading = isAnalyticsDealsLoading && !!selectedPeriod && chartByDay.length === 0
    const isSellerAnalyticsLoading = isAnalyticsDealsBySellerLoading && !!selectedPeriod && dealsBySeller.length === 0
    const isSourceAnalyticsLoading = isAnalyticsDealsBySourceLoading && !!selectedPeriod && dealsBySource.length === 0
    const isTableLoading = isAnalyticsDealsLoading && !!selectedPeriod && tableRows.length === 0

    const sourceTotals = useMemo(
        () =>
            dealsBySource.reduce(
                (acc, row) => ({
                    count: acc.count + row.count,
                    sum: acc.sum + row.sum,
                }),
                { count: 0, sum: 0 },
            ),
        [dealsBySource],
    )

    const sortedDealsBySeller = useMemo(() => {
        const rows = [...dealsBySeller]
        rows.sort((a, b) => {
            const direction = sellerSortDirection === 'asc' ? 1 : -1
            switch (sellerSortField) {
                case 'manager':
                    return a.manager.localeCompare(b.manager) * direction
                case 'count':
                    return (a.count - b.count) * direction
                case 'sum':
                    return (a.sum - b.sum) * direction
                default:
                    return 0
            }
        })
        return rows
    }, [dealsBySeller, sellerSortDirection, sellerSortField])

    const sortedDealsBySource = useMemo(() => {
        const rows = [...dealsBySource]
        rows.sort((a, b) => {
            const direction = sourceSortDirection === 'asc' ? 1 : -1
            switch (sourceSortField) {
                case 'source':
                    return marketplaceLabel(a.source).localeCompare(marketplaceLabel(b.source)) * direction
                case 'count':
                    return (a.count - b.count) * direction
                case 'sum':
                    return (a.sum - b.sum) * direction
                default:
                    return 0
            }
        })
        return rows
    }, [dealsBySource, sourceSortDirection, sourceSortField])

    const sortedRows = useMemo(() => {
        const rows = [...tableRows]
        rows.sort((a, b) => {
            const direction = sortDirection === 'asc' ? 1 : -1
            switch (sortField) {
                case 'date':
                    return a.date.localeCompare(b.date) * direction
                case 'marketplace':
                    return marketplaceLabel(a.marketplace).localeCompare(marketplaceLabel(b.marketplace)) * direction
                case 'count':
                    return (a.count - b.count) * direction
                case 'sum':
                    return (a.sum - b.sum) * direction
                default:
                    return 0
            }
        })
        return rows
    }, [sortDirection, sortField, tableRows])

    const tableTotals = useMemo(
        () =>
            sortedRows.reduce(
                (acc, row) => ({
                    count: acc.count + row.count,
                    sum: acc.sum + row.sum,
                }),
                { count: 0, sum: 0 },
            ),
        [sortedRows],
    )

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSortField(field)
        setSortDirection('asc')
    }

    const sortIndicator = (field: SortField) => (sortField === field ? (sortDirection === 'asc' ? '^' : 'v') : '')

    const toggleSellerSort = (field: SellerSortField) => {
        if (sellerSortField === field) {
            setSellerSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSellerSortField(field)
        setSellerSortDirection('asc')
    }

    const sellerSortIndicator = (field: SellerSortField) =>
        sellerSortField === field ? (sellerSortDirection === 'asc' ? '^' : 'v') : ''

    const toggleSourceSort = (field: SourceSortField) => {
        if (sourceSortField === field) {
            setSourceSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSourceSortField(field)
        setSourceSortDirection('asc')
    }

    const sourceSortIndicator = (field: SourceSortField) =>
        sourceSortField === field ? (sourceSortDirection === 'asc' ? '^' : 'v') : ''

    const toggleMarketplace = (marketplace: MarketplacesKeys) => {
        setSelectedMarketplaces(prev =>
            prev.includes(marketplace) ? prev.filter(item => item !== marketplace) : [...prev, marketplace],
        )
    }

    const toggleAllMarketplaces = () => {
        setSelectedMarketplaces(prev => (prev.length === allMarketplaces.length ? [] : allMarketplaces))
    }

    const shiftMonth = (diff: number) => {
        const [year, month] = monthValue.split('-').map(Number)
        if (!year || !month) return
        const next = new Date(year, month - 1 + diff, 1)
        setMonthValue(formatMonthValue(next))
    }

    const resetMonth = () => setMonthValue(getCurrentMonthValue())

    return (
        <div className={styles.wrap}>
            <SalesText text="Аналитика по сделкам" fontSize={fontSizes.xxl} fontWeight={fontWeights.bold} />
            <div className={styles.breadcrumbs}>
                <Link
                    className={styles.breadcrumbLink}
                    to={getAdminNavigationPath(adminRouteAlias.analytics.dashboard)}
                >
                    Аналитика
                </Link>
                <span className={styles.breadcrumbSeparator}>/</span>
                <span className={styles.breadcrumbCurrent}>Аналитика по сделкам</span>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Фильтры</div>
                <div className={styles.filters}>
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Период</label>
                        <select
                            className={styles.input}
                            value={periodMode}
                            onChange={e => setPeriodMode(e.target.value as PeriodMode)}
                        >
                            <option value="month">Месяц</option>
                            <option value="range">Диапазон дат</option>
                        </select>
                    </div>
                    {periodMode === 'month' ? (
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>Месяц</label>
                            <div className={styles.monthControls}>
                                <SalesButton
                                    borderRadius={10}
                                    backgroundColor={colors.grayDark}
                                    paddingHorizontalPixels={12}
                                    paddingVerticalPixels={7}
                                    onClick={() => shiftMonth(-1)}
                                >
                                    <ChevronLeft size={18} color={colors.white} />
                                </SalesButton>
                                <SalesButton
                                    borderRadius={10}
                                    backgroundColor={colors.grayDark}
                                    paddingHorizontalPixels={12}
                                    paddingVerticalPixels={7}
                                    onClick={resetMonth}
                                >
                                    <SalesText
                                        className={styles.monthLabel}
                                        text={monthLabel(monthValue)}
                                        fontSize={fontSizes.s}
                                        color={colors.white}
                                    />
                                </SalesButton>
                                <SalesButton
                                    borderRadius={10}
                                    backgroundColor={colors.grayDark}
                                    paddingHorizontalPixels={12}
                                    paddingVerticalPixels={7}
                                    onClick={() => shiftMonth(1)}
                                >
                                    <ChevronRight size={18} color={colors.white} />
                                </SalesButton>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.rangeGroup}>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Дата от</label>
                                <input
                                    className={styles.input}
                                    type="date"
                                    value={rangeFrom}
                                    onChange={e => setRangeFrom(e.target.value)}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Дата до</label>
                                <input
                                    className={styles.input}
                                    type="date"
                                    value={rangeTo}
                                    onChange={e => setRangeTo(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Статус сделки</label>
                        <select
                            className={styles.input}
                            value={selectedStatus}
                            onChange={e => setSelectedStatus(e.target.value as DealStatus)}
                        >
                            {Object.values(DealStatus).map(status => (
                                <option key={status} value={status}>
                                    {dealStatusDictData?.data?.[status]?.name || status}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Тип даты</label>
                        <select
                            className={styles.input}
                            value={dateType}
                            onChange={e => setDateType(e.target.value as DateType)}
                        >
                            <option value="created">Дата создания</option>
                            <option value="sold">Дата продажи</option>
                        </select>
                    </div>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Маркетплейсы</label>
                    <div className={styles.checkboxList}>
                        <label className={styles.checkboxItem}>
                            <input type="checkbox" checked={allMarketplacesSelected} onChange={toggleAllMarketplaces} />
                            <span>Все</span>
                        </label>
                        {allMarketplaces.map(marketplace => (
                            <label key={marketplace} className={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    checked={selectedMarketplaces.includes(marketplace)}
                                    onChange={() => toggleMarketplace(marketplace)}
                                />
                                <span>{Marketplaces[marketplace]}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
            {periodMode === 'range' && !selectedPeriod && (
                <div className={styles.section}>
                    <div className={styles.emptyState}>Выберите корректный диапазон дат</div>
                </div>
            )}
            {isError && (
                <div className={styles.section}>
                    <div className={styles.emptyState}>Ошибка загрузки данных аналитики</div>
                </div>
            )}
            {isAnalyticsDealsBySellerError && (
                <div className={styles.section}>
                    <div className={styles.emptyState}>Ошибка загрузки аналитики по ответственным</div>
                </div>
            )}
            {isAnalyticsDealsBySourceError && (
                <div className={styles.section}>
                    <div className={styles.emptyState}>Ошибка загрузки аналитики по источникам</div>
                </div>
            )}

            <div className={styles.splitSections}>
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Кол-во сделок по дням ({dealsCountLabel})</div>
                    <div className={styles.chart}>
                        {isChartsLoading ? (
                            <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartByDay}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        interval={1}
                                        tickFormatter={formatAxisDate}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                            borderRadius: 10,
                                            border: 'none',
                                            color: colors.white,
                                        }}
                                        itemStyle={{ color: colors.white }}
                                        labelStyle={{ color: colors.white }}
                                    />
                                    <Bar dataKey="count" fill={colors.blueLight} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Сумма сделок по дням ({dealsSumLabel})</div>
                    <div className={styles.chart}>
                        {isChartsLoading ? (
                            <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartByDay}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        interval={1}
                                        tickFormatter={formatAxisDate}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                            borderRadius: 10,
                                            border: 'none',
                                            color: colors.white,
                                        }}
                                        itemStyle={{ color: colors.white }}
                                        labelStyle={{ color: colors.white }}
                                    />
                                    <Bar dataKey="sum" fill={colors.pinkDark} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.splitSections}>
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Кол-во сделок по маркетплейсам ({dealsCountLabel})</div>
                    <div className={styles.chart}>
                        {isChartsLoading ? (
                            <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={dealsByMarketplace}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="marketplace"
                                        tickFormatter={(value: string) => marketplaceLabel(value)}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip
                                        formatter={(value: number) => value}
                                        contentStyle={{
                                            backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                            borderRadius: 10,
                                            border: 'none',
                                            color: colors.white,
                                        }}
                                        itemStyle={{ color: colors.white }}
                                        labelStyle={{ color: colors.white }}
                                    />
                                    <Bar dataKey="count" fill={colors.green} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Сумма сделок по маркетплейсам ({dealsSumLabel})</div>
                    <div className={styles.chart}>
                        {isChartsLoading ? (
                            <div className={`${styles.skeletonPulse} ${styles.skeletonChart}`} />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={dealsByMarketplace}>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="marketplace"
                                        tickFormatter={(value: string) => marketplaceLabel(value)}
                                    />
                                    <YAxis />
                                    <Tooltip
                                        formatter={(value: number) => value}
                                        contentStyle={{
                                            backgroundColor: 'rgba(96, 97, 108, 0.85)',
                                            borderRadius: 10,
                                            border: 'none',
                                            color: colors.white,
                                        }}
                                        itemStyle={{ color: colors.white }}
                                        labelStyle={{ color: colors.white }}
                                    />
                                    <Bar dataKey="sum" fill={colors.purple} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.splitSections}>
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Аналитика по ответственным</div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => toggleSellerSort('manager')}
                                        >
                                            Менеджер{' '}
                                            <span className={styles.sortIndicator}>
                                                {sellerSortIndicator('manager')}
                                            </span>
                                        </button>
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => toggleSellerSort('count')}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            Кол. сделок{' '}
                                            <span className={styles.sortIndicator}>{sellerSortIndicator('count')}</span>
                                        </button>
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => toggleSellerSort('sum')}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            Сумма сделок{' '}
                                            <span className={styles.sortIndicator}>{sellerSortIndicator('sum')}</span>
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isSellerAnalyticsLoading &&
                                    Array.from({ length: 4 }).map((_, index) => (
                                        <tr key={`seller-skeleton-row-${index}`}>
                                            <td>
                                                <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                            </td>
                                            <td>
                                                <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                            </td>
                                            <td>
                                                <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                            </td>
                                        </tr>
                                    ))}
                                {!isSellerAnalyticsLoading &&
                                    sortedDealsBySeller.map(row => (
                                        <tr key={row.manager}>
                                            <td>{row.manager}</td>
                                            <td style={{ textAlign: 'right' }}>{row.count}</td>
                                            <td style={{ textAlign: 'right' }}>{row.sum.toLocaleString('ru-RU')}</td>
                                        </tr>
                                    ))}
                                {!isSellerAnalyticsLoading && dealsBySeller.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className={styles.emptyState}>
                                            Нет данных за выбранный период
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Аналитика по источникам</div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => toggleSourceSort('source')}
                                        >
                                            Источник{' '}
                                            <span className={styles.sortIndicator}>
                                                {sourceSortIndicator('source')}
                                            </span>
                                        </button>
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => toggleSourceSort('count')}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            Кол-во{' '}
                                            <span className={styles.sortIndicator}>{sourceSortIndicator('count')}</span>
                                        </button>
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                        <button
                                            className={styles.sortButton}
                                            onClick={() => toggleSourceSort('sum')}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            Сумма{' '}
                                            <span className={styles.sortIndicator}>{sourceSortIndicator('sum')}</span>
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isSourceAnalyticsLoading &&
                                    Array.from({ length: 4 }).map((_, index) => (
                                        <tr key={`source-skeleton-row-${index}`}>
                                            <td>
                                                <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                            </td>
                                            <td>
                                                <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                            </td>
                                            <td>
                                                <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                            </td>
                                        </tr>
                                    ))}
                                {!isSourceAnalyticsLoading &&
                                    sortedDealsBySource.map(row => (
                                        <tr key={row.source}>
                                            <td>{marketplaceLabel(row.source)}</td>
                                            <td style={{ textAlign: 'right' }}>{row.count}</td>
                                            <td style={{ textAlign: 'right' }}>{row.sum.toLocaleString('ru-RU')}</td>
                                        </tr>
                                    ))}
                                {!isSourceAnalyticsLoading && dealsBySource.length > 0 && (
                                    <tr>
                                        <td>
                                            <b>Итого</b>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <b>{sourceTotals.count}</b>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <b>{sourceTotals.sum.toLocaleString('ru-RU')}</b>
                                        </td>
                                    </tr>
                                )}
                                {!isSourceAnalyticsLoading && dealsBySource.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className={styles.emptyState}>
                                            Нет данных за выбранный период
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Данные по сделкам</div>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>
                                    <button className={styles.sortButton} onClick={() => toggleSort('date')}>
                                        Дата <span className={styles.sortIndicator}>{sortIndicator('date')}</span>
                                    </button>
                                </th>
                                <th>
                                    <button className={styles.sortButton} onClick={() => toggleSort('marketplace')}>
                                        Источник сделки{' '}
                                        <span className={styles.sortIndicator}>{sortIndicator('marketplace')}</span>
                                    </button>
                                </th>
                                <th style={{ textAlign: 'right' }}>
                                    <button
                                        className={styles.sortButton}
                                        onClick={() => toggleSort('count')}
                                        style={{ marginLeft: 'auto' }}
                                    >
                                        Кол-во сделок{' '}
                                        <span className={styles.sortIndicator}>{sortIndicator('count')}</span>
                                    </button>
                                </th>
                                <th style={{ textAlign: 'right' }}>
                                    <button
                                        className={styles.sortButton}
                                        onClick={() => toggleSort('sum')}
                                        style={{ marginLeft: 'auto' }}
                                    >
                                        Сумма сделок{' '}
                                        <span className={styles.sortIndicator}>{sortIndicator('sum')}</span>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isTableLoading &&
                                Array.from({ length: 6 }).map((_, index) => (
                                    <tr key={`skeleton-row-${index}`}>
                                        <td>
                                            <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                        </td>
                                        <td>
                                            <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                        </td>
                                        <td>
                                            <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                        </td>
                                        <td>
                                            <div className={`${styles.skeletonPulse} ${styles.skeletonTableRow}`} />
                                        </td>
                                    </tr>
                                ))}
                            {!isTableLoading &&
                                sortedRows.map(row => (
                                    <tr key={`${row.date}-${row.marketplace}`}>
                                        <td>{row.date}</td>
                                        <td>{marketplaceLabel(row.marketplace)}</td>
                                        <td style={{ textAlign: 'right' }}>{row.count}</td>
                                        <td style={{ textAlign: 'right' }}>{row.sum.toLocaleString('ru-RU')}</td>
                                    </tr>
                                ))}
                            {!isTableLoading && sortedRows.length > 0 && (
                                <tr>
                                    <td>
                                        <b>Итого</b>
                                    </td>
                                    <td></td>
                                    <td style={{ textAlign: 'right' }}>
                                        <b>{tableTotals.count}</b>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <b>{tableTotals.sum.toLocaleString('ru-RU')}</b>
                                    </td>
                                </tr>
                            )}
                            {!isTableLoading && sortedRows.length === 0 && (
                                <tr>
                                    <td colSpan={4} className={styles.emptyState}>
                                        Нет данных за выбранный период
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default AnalyticsDeals
